import React, { memo, useCallback, useRef, useState } from "react";
import SettingsTabPage from "../../components/feature/settings/settings-tab-page";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from "react-native";
import { useSettingsData } from "../../contexts/SettingsDataContext";
import { useToast } from "../../contexts/ToastContext";
import { E164Number } from "../../types/agent-settings";
import {
  formatPhoneNumberIntl,
  isPossiblePhoneNumber,
} from "react-phone-number-input";
import SettingsItem from "../../components/feature/settings/common/settings-item";
import { useActiveAgent } from "../../contexts/ActiveAgentContext";
import StyledText from "../../components/common/ui/styled-text";
import PhoneInput from "react-phone-number-input/input";
import { Ban, Plus, Search } from "lucide-react-native";
import { useFocusEffect } from "expo-router";
import { useViewport } from "../../hooks/useViewport";

type DeleteConfirmationState = {
  number: E164Number | null;
  isConfirming: boolean;
};

const BlockedTab = () => {
  const {
    blockedCallers,
    handleAddBlockedCaller,
    handleDeleteBlockedCaller,
    loadSettings,
  } = useSettingsData();
  const { activeAgent } = useActiveAgent();
  const { showErrorToast } = useToast();
  const newNumberInputRef = useRef<any>(null);
  const scrollViewRef = useRef<ScrollView>(null);

  useFocusEffect(
    useCallback(() => {
      loadSettings();
    }, [])
  );

  const [isAdding, setIsAdding] = useState(false);
  const [newNumber, setNewNumber] = useState<string | undefined>();

  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isAddLoading, setIsAddLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmationState>({
      number: null,
      isConfirming: false,
    });

  const filteredBlockedNumbers = blockedCallers.filter((num) =>
    num.includes(searchQuery)
  );

  // const getFlagUrlFromNumber = (number: E164Number) => {
  //   const phoneNumber = parsePhoneNumber(number);
  //   if (phoneNumber && phoneNumber.country) {
  //     return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${phoneNumber.country.toUpperCase()}.svg`;
  //   }
  //   return null;
  // };

  const isNumberValid = useCallback(() => {
    return isPossiblePhoneNumber(newNumber?.trim() || "");
  }, [newNumber]);

  const handleAdd = useCallback(async () => {
    if (!newNumber || !isNumberValid()) return;

    if (blockedCallers.includes(newNumber)) {
      showErrorToast("Number already blocked.");
      return;
    }

    setIsAddLoading(true);
    try {
      const success = await handleAddBlockedCaller(newNumber);
      if (success) {
        setNewNumber(undefined);
        setIsAdding(false);
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
      }
    } finally {
      setIsAddLoading(false);
    }
  }, [handleAddBlockedCaller, blockedCallers, newNumber, isNumberValid]);

  const handleDelete = useCallback(
    async (num: E164Number) => {
      setIsDeleteLoading(true);
      try {
        const success = await handleDeleteBlockedCaller(num);
        if (success) {
          setDeleteConfirmation({
            number: null,
            isConfirming: false,
          });
        }
      } finally {
        setIsDeleteLoading(false);
      }
    },
    [handleDeleteBlockedCaller, setIsDeleteLoading]
  );

  const isNumberEmpty = useCallback(() => {
    const trimmedNumber = newNumber?.trim();
    return !trimmedNumber || trimmedNumber.length === 0;
  }, [newNumber]);

  const { isMobile, height } = useViewport();
  const growNum = isMobile ? 3 : 5;
  const isGrowMode = blockedCallers.length > growNum;
  const maxHeight = Math.max(400, height - 245 - 200);

  return (
    <SettingsTabPage contentContainerStyle={isGrowMode ? { flexGrow: 1 } : {}}>
      <View
        className="flex-1 px-3 py-5 sm:py-5 sm:px-5 bg-white rounded-xl shadow-sm border border-gray-100"
        style={{
          maxHeight: blockedCallers.length > 0 ? maxHeight : "fit-content",
        }}
      >
        <SettingsItem
          title="Blocked Callers"
          description="Manage your list of blocked phone numbers."
          icon={Ban}
          withFlex={true}
        >
          <View className="flex-1">
            {/* Search Box */}
            {blockedCallers.length > 0 && (
              <View className="flex-shrink-0">
                <View className="flex-row items-center px-1 py-2 gap-2 border border-gray-200 rounded-md mb-3">
                  <Search size={16} color="#6B7280" />
                  <TextInput
                    placeholder="Search..."
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    className="flex-1 text-sm"
                    clearButtonMode="while-editing"
                    placeholderTextColor="#6B7280"
                    inputMode="numeric"
                  />
                </View>
              </View>
            )}

            {blockedCallers.length > 0 && (
              <View
                className={`overflow-hidden ${isGrowMode ? `flex-1` : ""}`}
                style={{
                  maxHeight: isGrowMode ? maxHeight : "inherit",
                }}
              >
                <ScrollView
                  className="flex-1"
                  ref={scrollViewRef}
                  contentContainerStyle={{ paddingHorizontal: 4 }}
                >
                  {blockedCallers.length > 0 &&
                  filteredBlockedNumbers.length === 0 ? (
                    <View className="flex-1 items-center justify-center">
                      <StyledText className="text-gray-500">
                        No results found.
                      </StyledText>
                    </View>
                  ) : (
                    filteredBlockedNumbers.map((num) => (
                      <View
                        key={num}
                        className="flex-row items-center justify-between py-2 border-b border-gray-200"
                      >
                        <View className="flex-row items-center gap-3">
                          {/* {getFlagUrlFromNumber(num) && (
                            <Image
                              source={{ uri: getFlagUrlFromNumber(num)! }}
                              className="w-6 h-4 object-cover"
                            />
                          )} */}
                          <StyledText className="text-gray-900 font-medium">
                            {formatPhoneNumberIntl(num)}
                          </StyledText>
                        </View>
                        <View className="flex-row items-center">
                          {deleteConfirmation.number === num ? (
                            <>
                              <Pressable
                                onPress={() => handleDelete(num)}
                                className="px-3 py-2 bg-red-100 rounded-md mr-2 hover:bg-red-200 outline-gray-500"
                              >
                                {isDeleteLoading ? (
                                  <View className="w-[53px] py-[1px] items-center">
                                    <ActivityIndicator
                                      size={16}
                                      color="#b91c1c"
                                    />
                                  </View>
                                ) : (
                                  <StyledText className="text-red-700">
                                    Confirm
                                  </StyledText>
                                )}
                              </Pressable>
                              <Pressable
                                onPress={() =>
                                  setDeleteConfirmation({
                                    number: null,
                                    isConfirming: false,
                                  })
                                }
                                className="px-3 py-2 bg-gray-100 rounded-md hover:bg-gray-200 outline-gray-500"
                              >
                                <StyledText className="text-gray-700">
                                  Cancel
                                </StyledText>
                              </Pressable>
                            </>
                          ) : (
                            <Pressable
                              onPress={() =>
                                setDeleteConfirmation({
                                  number: num,
                                  isConfirming: true,
                                })
                              }
                              className="px-3 py-2 hover:bg-gray-100 rounded-md outline-gray-500"
                            >
                              <View className="flex-row items-center gap-[6px]">
                                <StyledText className="text-gray-700">
                                  Unblock
                                </StyledText>
                              </View>
                            </Pressable>
                          )}
                        </View>
                      </View>
                    ))
                  )}
                </ScrollView>
              </View>
            )}

            {/* Add Number */}
            <View className="flex-shrink-0 mt-4">
              {isAdding ? (
                <View className="flex flex-col sm:flex-row items-end sm:items-center gap-3 mb-[6px]">
                  <PhoneInput
                    ref={newNumberInputRef}
                    value={newNumber}
                    onChange={(text) => setNewNumber(text)}
                    className={`w-full sm:flex-1 p-2 border rounded-md ${
                      isNumberEmpty()
                        ? "border-gray-300"
                        : !isNumberValid()
                          ? "border-red-500"
                          : "border-green-500"
                    }`}
                    defaultCountry={activeAgent?.country || "US"}
                    placeholder="Enter phone number to block"
                  />
                  <View className="flex-row items-center gap-2 flex-shrink-0">
                    <Pressable
                      onPress={handleAdd}
                      className={`px-4 py-3 rounded-md bg-gray-200 hover:bg-gray-300 ${
                        !isNumberValid() ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={!isNumberValid() || isAddLoading}
                    >
                      {isAddLoading ? (
                        <View className="w-[32px] py-[1px] items-center">
                          <ActivityIndicator size={16} color="#111827" />
                        </View>
                      ) : (
                        <StyledText className="text-gray-900 font-medium">
                          Save
                        </StyledText>
                      )}
                    </Pressable>
                    <Pressable
                      onPress={() => {
                        setIsAdding(false);
                        setNewNumber(undefined);
                      }}
                      className="flex-1 sm:flex-initial px-3 py-3 rounded-md bg-white hover:bg-gray-100 border border-gray-200"
                    >
                      <StyledText className="text-gray-900 font-medium">
                        Cancel
                      </StyledText>
                    </Pressable>
                  </View>
                </View>
              ) : (
                <Pressable
                  onPress={() => {
                    setIsAdding(true);
                    setTimeout(() => {
                      newNumberInputRef.current?.focus();
                    }, 100);
                  }}
                  className="w-full mb-2 flex-row items-center justify-center px-3 py-2 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-700"
                >
                  <Plus size={16} color="#111827" className="mr-1" />
                  <StyledText className="font-semibold text-gray-900">
                    Add Number
                  </StyledText>
                </Pressable>
              )}
            </View>
          </View>
        </SettingsItem>
      </View>
    </SettingsTabPage>
  );
};

export default memo(BlockedTab);
